import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';

import { Button, Card, Input, useToast } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';

export function MagicLinkRequest() {
  const { slug } = useParams<{ slug: string }>();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);

  const send = useMutation({
    mutationFn: async () => {
      await api.post('/public/auth/magic-link', { orgSlug: slug, email });
    },
    onSuccess: () => setDone(true),
    onError: (err) => toast.push(errorMessage(err), 'error'),
  });

  return (
    <div className="min-h-screen bg-cream-100 px-6 py-10 flex items-center justify-center">
      <Card className="p-8 max-w-md w-full space-y-4">
        <h1 className="font-display text-2xl">Sign in</h1>
        {done ? (
          <p>If your email is on file, a sign-in link is on its way.</p>
        ) : (
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              send.mutate();
            }}
            className="space-y-4"
          >
            <Input
              type="email"
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Button type="submit" loading={send.isPending} fullWidth>
              Email me a sign-in link
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}

export function MagicLinkVerify() {
  const { slug } = useParams<{ slug: string }>();
  const sp = new URLSearchParams(window.location.search);
  const token = sp.get('token') ?? '';
  const [status, setStatus] = useState<'pending' | 'ok' | 'err'>('pending');

  useState(() => {
    void (async () => {
      try {
        await api.get(`/public/auth/magic-link/${encodeURIComponent(token)}`);
        setStatus('ok');
        window.location.href = `/m/${slug}`;
      } catch {
        setStatus('err');
      }
    })();
  });

  return (
    <div className="min-h-screen bg-cream-100 px-6 py-10 flex items-center justify-center">
      <Card className="p-8 max-w-md w-full text-center">
        {status === 'pending' && <p>Signing you in…</p>}
        {status === 'err' && <p>This link is invalid or expired. Please request a new one.</p>}
      </Card>
    </div>
  );
}
